import React, { useEffect, useRef, useState, ForwardedRef, forwardRef, Fragment } from 'react'
import {
  Table as MuiTable,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Box,
  Collapse,
  IconButton,
  PaperProps,
  SxProps,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import Pagination from './Pagination'
import { useDerivedState } from '../../../Hooks/UseDerivedState'
import { useCustomTranslation } from '../../../Hooks/useCustomTranslation'
import { CurrentSort, ShellTableProps } from '../types'
import SpacerRow from './SpaceRow'
import TableHead from './TableHead'
import useStyles from '../styles'
import { J1DownarrowD, J1ToparrowD } from '@e3dc-react/icons'

const PaperTableBody: React.FC<PaperProps<'tbody'>> = props => <Paper component={'tbody'} {...props} />

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TableComplete = <T extends { [key: string]: any }>(
  {
    data,
    header: unfilteredHeader,
    onSelect,
    className,
    headerClassName,
    bodyClassName,
    rowClassName,
    footerClassName,
    resultLimit,
    setResultLimit,
    resultPage,
    setResultPage,
    dataTotal,
    customFunctionalities,
    bodyElevation = 1,
    headerElevation = 1,
    footerElevation = 1,
    disablePagination,
    size,
    coloredColumn,
    initialSort,
    setSortedCsvData,
    onSortChange,
    manualPagination = false,
    collapsibleContent,
    allowOtherCellColors,
    noDataText,
    stickyHeader,
    stickyFirstColumn,
    stickyLastColumn,
    rowId,
  }: /** The ref attribute is added in the props in order for typescript to identify the forwarded ref and not throw errors.
   *  This is done in order to preserve the generic type of the TableComplete function and let it work as before in the other
   *  components that use it.
   *  Important note: the ref is not taken from the props, it is provided by the forwardRef function used at the export level
   *  of this component
   */
  ShellTableProps<T> & {
    onSortChange?: (newSort: CurrentSort<T> | undefined) => void
    allowOtherCellColors?: boolean
    stickyHeader?: { tableHeight: number | string }
    stickyFirstColumn?: boolean
    stickyLastColumn?: boolean
  } & {
    ref?: React.Ref<HTMLElement>
  },
  ref: ForwardedRef<HTMLElement>
): React.ReactElement => {
  if ((stickyFirstColumn ?? stickyLastColumn) && collapsibleContent) {
    throw new Error('Sticky columns cannot be used alongside collapsible content')
  }

  if (unfilteredHeader.some(column => column.isColumnHidden && column.value === initialSort?.name)) {
    throw new Error('Cannot set initialSort on a hidden column')
  }

  const header = unfilteredHeader.filter(headerItem => !headerItem.isColumnHidden)

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})
  const [page, setPage] = useDerivedState(resultPage ?? 0)
  const [limit, setLimit] = useDerivedState(resultLimit ?? 10)
  const [currentSort, setCurrentSort] = useState<CurrentSort<T> | undefined>(() => {
    if (initialSort) {
      return initialSort
    }

    return header.find(head => head.sortFunction)
      ? {
          name: header.find(head => head.sortFunction)!.value,
          direction: 'asc',
        }
      : undefined
  })

  const lastSort = useRef<CurrentSort<T> | undefined>()
  if (lastSort.current?.name !== currentSort?.name || lastSort.current?.direction !== currentSort?.direction) {
    lastSort.current = currentSort
    onSortChange?.(currentSort)
  }

  const sortedData = manualPagination
    ? data
    : (() => {
        const currentlySortedHeaderItem = header.find(item => item.value === currentSort?.name)
        return currentlySortedHeaderItem && currentSort
          ? [...data].sort((itemA, itemB) =>
              currentSort.direction === 'asc'
                ? currentlySortedHeaderItem.sortFunction!(itemA[currentSort.name], itemB[currentSort.name], currentlySortedHeaderItem.value)
                : currentlySortedHeaderItem.sortFunction!(itemB[currentSort.name], itemA[currentSort.name], currentlySortedHeaderItem.value)
            )
          : data
      })()

  const paginatedData = disablePagination ? sortedData : sortedData.slice(page * limit, (page + 1) * limit)

  const shownData = manualPagination ? data : paginatedData
  const selectable = Boolean(onSelect)
  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles({ coloredColumn, allowOtherCellColors })

  /**
   * When either first(leftmost) or last(rightmost) column is sticky, we emphasize them by separating those columns with
   * a vertical divider(border) between the sticky column and the rest of the columns. The spacer between table header
   * and first row in that case breaks the divider, so we opt for not showing it unless no columns are sticky.
   *
   * Otherwise, show spacer to reduce visual clutter of the table.
   */
  const showSpacerBetweenHeaderAndBody = !(stickyLastColumn ?? stickyFirstColumn)

  useEffect(() => {
    if (setSortedCsvData) {
      setSortedCsvData(paginatedData, sortedData)
    }
  }, [paginatedData, sortedData, setSortedCsvData])

  useEffect(() => {
    /** whenever new data arrives, collapse rows(e.g. new page, sort order or filter) */
    setExpandedRows({})
  }, [data])
  return (
    <Box ref={ref}>
      <TableContainer
        className={cx(className, 'container-root')}
        sx={{
          maxHeight: stickyHeader ? stickyHeader?.tableHeight : undefined,
        }}
      >
        <MuiTable key="body-table" className={classes.table} classes={{ root: bodyClassName }} size={size}>
          <TableHead
            className={headerClassName}
            headerItems={[
              ...(collapsibleContent ? [{ label: '', value: '', isSortable: false }] : []),
              ...header.map(item => ({
                label: item.label ?? '',
                value: item.value,
                isSortable: item.isSortable ?? true,
                isColumnHidden: item.isColumnHidden,
                width: item.width,
              })),
            ]}
            currentSort={currentSort}
            setCurrentSort={setCurrentSort}
            customFunctionalities={customFunctionalities}
            elevation={headerElevation}
            stickyHeader={Boolean(stickyHeader)}
            stickyFirstColumn={stickyFirstColumn}
            stickyLastColumn={stickyLastColumn}
          />
          <TableBody className={bodyClassName} component={PaperTableBody} elevation={bodyElevation}>
            {showSpacerBetweenHeaderAndBody ? <SpacerRow cellCount={shownData.length} /> : null}

            {shownData.length === 0 ? (
              <TableRow className={cx(classes.lastRow, rowClassName)}>
                <TableCell align="center" colSpan={header.length + (customFunctionalities?.length ?? 0)}>
                  {noDataText ?? t('shell.noData')}
                </TableCell>
              </TableRow>
            ) : (
              shownData.map((row, rowIndex) => (
                <Fragment key={rowId ? rowId(row) : rowIndex}>
                  <TableRow
                    hover={selectable}
                    onClick={() => {
                      if (selectable) onSelect?.(row)
                    }}
                    className={cx(rowClassName, {
                      [classes.lastRow]: rowIndex === shownData.length - 1,
                    })}
                    style={{ cursor: selectable ? 'pointer' : 'default' }}
                  >
                    {collapsibleContent && (
                      <TableCell key={`expand-row-${rowIndex}`}>
                        <IconButton
                          onClick={() => {
                            setExpandedRows(prev => ({ ...prev, [rowIndex]: !prev[rowIndex] }))
                          }}
                          data-qa={`colapse-button-row-${rowIndex}`}
                          size="large"
                        >
                          {expandedRows[rowIndex] ? <J1ToparrowD /> : <J1DownarrowD />}
                        </IconButton>
                      </TableCell>
                    )}

                    {header.map((item, columnIndex) => {
                      const isFirstColumn = columnIndex === 0
                      const isLastColumn = columnIndex === header.length - 1

                      const shouldFirstColumnBeSticky = isFirstColumn && stickyFirstColumn
                      const shouldLastColumnBeSticky = isLastColumn && stickyLastColumn

                      const firstColumnStickySx: SxProps<Theme> = {
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        borderRight: theme => `1px solid ${theme.palette.grey.grey06}`,
                      }

                      const lastColumnStickySx: SxProps<Theme> = {
                        position: 'sticky',
                        right: 0,
                        zIndex: 1,
                        borderLeft: theme => `1px solid ${theme.palette.grey.grey06}`,
                      }

                      return (
                        <TableCell
                          key={columnIndex}
                          className={cx(classes.tableCell, {
                            [classes.highlightedRow]: coloredColumn && rowIndex % 2 !== 0,
                          })}
                          width={item.width}
                          sx={{
                            minWidth: item.width,
                            backgroundColor: theme => theme.palette.background.paper,
                            ...(shouldFirstColumnBeSticky ? firstColumnStickySx : {}),
                            ...(shouldLastColumnBeSticky ? lastColumnStickySx : {}),
                          }}
                        >
                          {item.valueRender ? item.valueRender(row[item.value], columnIndex, row, rowIndex) : row[item.value]}
                        </TableCell>
                      )
                    })}
                    {customFunctionalities?.map(value => {
                      return <TableCell key={rowIndex}>{value[rowIndex]}</TableCell>
                    })}
                  </TableRow>

                  {collapsibleContent ? (
                    <TableRow>
                      <TableCell colSpan={header.length + 1} style={{ padding: 0, margin: 0, border: 'none' }}>
                        <Collapse in={expandedRows[rowIndex]} timeout="auto" unmountOnExit>
                          {collapsibleContent(row, rowIndex)}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {!disablePagination && (
        <Pagination
          className={cx(footerClassName, classes.pagination)}
          limit={resultLimit ?? limit}
          setLimit={setResultLimit ?? setLimit}
          page={resultPage ?? page}
          setPage={setResultPage ?? setPage}
          count={dataTotal || data.length}
          elevation={footerElevation}
        />
      )}
    </Box>
  )
}

export default forwardRef(TableComplete) as typeof TableComplete
